/**
 * LegalContent — renders a legal document (Terms of Use, Privacy Policy) from
 * the `legal.*` i18n tree, mirroring the mobile policy screen.
 */
import type { ComponentType } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ClockIcon, type IconProps } from '../../components/ui/icons';
import { MorePage } from '../more/MorePage';
import './support.css';

export interface LegalSectionConfig {
  key: string;
  uppercase?: boolean;
}

interface LegalSectionContent {
  heading?: string;
  body?: string[];
  bullets?: string[];
}

interface LegalContentProps {
  /** Key under `legal.` holding the document copy. */
  contentKey: 'termsOfUse' | 'privacyPolicy';
  sections: readonly LegalSectionConfig[];
  heroIcon: ComponentType<IconProps>;
}

export function LegalContent({ contentKey, sections, heroIcon: HeroIcon }: LegalContentProps) {
  const { t } = useTranslation();
  const title = t(`legal.${contentKey}.title`);

  return (
    <MorePage title={title}>
      <header className="legal-hero">
        <span className="legal-hero__icon" aria-hidden>
          <HeroIcon size={26} />
        </span>
        <h1 className="legal-hero__title">{title}</h1>
        <p className="legal-hero__subtitle">{t(`legal.${contentKey}.heroSubtitle`)}</p>
        <p className="legal-hero__date">
          <ClockIcon size={14} aria-hidden />
          {t('legal.effectiveDate')}
        </p>
      </header>

      <ol className="legal-sections">
        {sections.map((section, index) => {
          const content = t(`legal.${contentKey}.sections.${section.key}`, {
            returnObjects: true,
          }) as LegalSectionContent | string;
          // A missing key returns the key itself; skip rather than render it.
          if (typeof content === 'string') return null;

          return (
            <li
              key={section.key}
              className={`legal-section${section.uppercase ? ' legal-section--uppercase' : ''}`}
            >
              <div className="legal-section__header">
                <span className="legal-section__badge" aria-hidden>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h2 className="legal-section__heading">{content.heading}</h2>
              </div>
              <div className="legal-section__divider" aria-hidden />
              {content.body?.map((paragraph, paragraphIndex) => (
                <p className="legal-section__paragraph" key={paragraphIndex}>
                  {paragraph}
                </p>
              ))}
              {content.bullets && content.bullets.length > 0 && (
                <ul className="legal-section__bullets">
                  {content.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex}>{bullet}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>

      <footer className="legal-footer">
        <span>{t('legal.copyright', { year: new Date().getFullYear() })}</span>
        <span>{t(`legal.${contentKey}.footerContact`)}</span>
      </footer>
    </MorePage>
  );
}
